import { useState } from "react"
import { toast } from "sonner"

import { ApiError } from "@/api/client"
import { useCambiarPassword, useOrganizacion, useYo } from "@/api/queries"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { formatearNumero } from "@/shared/format"

/**
 * La organización de quien ha entrado, sus límites, y el cambio de contraseña.
 *
 * **Los límites se ven y no se tocan.** Los pone el dueño de la plataforma desde el
 * servidor, y no hay endpoint para cambiarlos: un cliente que pudiera subirse su propio
 * límite no tendría límite. Enseñarlos sí importa — un 429 sin saber cuál era el tope
 * es un misterio, y con el número es una decisión.
 */
export function OrganizationPage() {
  const org = useOrganizacion()
  const yo = useYo()

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Organización</h1>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Datos</CardTitle>
          </CardHeader>
          <CardContent>
            {org.isPending ? (
              <Skeleton className="h-24" />
            ) : org.isError ? (
              <p className="text-sm text-destructive">No se pudo cargar la organización.</p>
            ) : (
              <dl className="space-y-3 text-sm">
                <Fila etiqueta="Nombre">{org.data.name}</Fila>
                <Fila etiqueta="Identificador">
                  <code className="text-xs">{org.data.slug}</code>
                </Fila>
                <Fila etiqueta="Estado">
                  <Badge variant={org.data.status === "active" ? "secondary" : "destructive"}>
                    {org.data.status === "active" ? "activa" : "suspendida"}
                  </Badge>
                </Fila>
                {yo.data !== undefined && (
                  <Fila etiqueta="Tu sesión">
                    <span className="text-muted-foreground">{yo.data.role}</span>
                  </Fila>
                )}
              </dl>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Límites</CardTitle>
            <CardDescription>
              Los fija el operador de la plataforma. Escríbenos si necesitas más.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {org.isPending ? (
              <Skeleton className="h-24" />
            ) : org.isError ? (
              <p className="text-sm text-destructive">No se pudieron cargar los límites.</p>
            ) : (
              <dl className="space-y-3 text-sm">
                <Fila etiqueta="Peticiones por minuto">
                  <Limite
                    propio={org.data.limites.rate_per_minute}
                    efectivo={org.data.limites.rate_per_minute_efectivo}
                  />
                </Fila>
                <Fila etiqueta="En vuelo a la vez">
                  <Limite
                    propio={org.data.limites.max_concurrent}
                    efectivo={org.data.limites.max_concurrent_efectivo}
                  />
                </Fila>
              </dl>
            )}
          </CardContent>
        </Card>
      </div>

      <CambiarPassword />
    </div>
  )
}

function Fila({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-muted-foreground">{etiqueta}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  )
}

/**
 * `0` significa **sin límite** y `null` significa "el valor por defecto del
 * despliegue". Son cosas distintas y se dicen distinto: leerlas al revés es
 * exactamente el error que el backend evita no usando `or` en su comprobación.
 */
function Limite({ propio, efectivo }: { propio: number | null; efectivo: number }) {
  if (efectivo === 0) return <span className="tabular-nums">sin límite</span>
  return (
    <span className="tabular-nums">
      {formatearNumero(efectivo)}
      {propio === null && <span className="ml-2 text-xs text-muted-foreground">(estándar)</span>}
    </span>
  )
}

function CambiarPassword() {
  const [actual, setActual] = useState("")
  const [nueva, setNueva] = useState("")
  const cambiar = useCambiarPassword()

  const corta = nueva !== "" && nueva.length < 12

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    try {
      await cambiar.mutateAsync({ actual, nueva })
      // No hace falta volver a entrar aquí: el gateway cierra las OTRAS sesiones, no
      // esta. Aun así el token queda invalidado en el servidor, así que la siguiente
      // petición devolverá 401 y la aplicación llevará al login sola.
      toast.success("Contraseña cambiada. Se han cerrado tus otras sesiones.")
      setActual("")
      setNueva("")
    } catch (e) {
      toast.error(
        e instanceof ApiError && e.status === 401
          ? "La contraseña actual no coincide."
          : "No se pudo cambiar la contraseña.",
      )
    }
  }

  return (
    <Card className="max-w-md">
      <CardHeader>
        <CardTitle>Tu contraseña</CardTitle>
        <CardDescription>Cambiarla cierra tus demás sesiones abiertas.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={enviar} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="actual">Contraseña actual</Label>
            <Input
              id="actual"
              type="password"
              autoComplete="current-password"
              value={actual}
              onChange={(e) => setActual(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nueva">Nueva contraseña</Label>
            <Input
              id="nueva"
              type="password"
              autoComplete="new-password"
              value={nueva}
              onChange={(e) => setNueva(e.target.value)}
              required
            />
            {corta && (
              <p className="text-xs text-destructive">Necesita al menos 12 caracteres.</p>
            )}
          </div>
          <Button
            type="submit"
            disabled={cambiar.isPending || actual === "" || nueva.length < 12}
          >
            {cambiar.isPending ? "Cambiando…" : "Cambiar contraseña"}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
